import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DataTable, DataTableExpandedRows, DataTableFilterEvent, DataTableRowToggleEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Panel } from 'primereact/panel';
import { Toast } from 'primereact/toast';
import { Menubar } from 'primereact/menubar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { FilterMatchMode } from 'primereact/api';
import { ledgerApi, allocationsApi, quickbooksApi, vendorsApi, checksApi } from '../../api';
import { LedgerEntry, CheckAllocationInfo, DepositAllocationInfo, Vendor } from '../../types';
import { useDataTableState, usePersistentState, useAccountPreference, useAccountMenuItem } from '../../hooks';
import { formatDate, amountFilterFunction } from '../../utils';
import { TrustAccountSelectionDialog } from './TrustAccountSelectionDialog';
import { LedgerEntrySummaryDialog } from './LedgerEntrySummaryDialog';

interface QBSyncTabProps {
  clientId: string;
}

interface Allocation {
  id: string;
  ledger_entry_id: string;
  customer_id: string;
  customer_name?: string;
  amount: number;
  allocation_source: string;
  qb_updated_at: string | null;
}

type AllocationData = {
  type: 'check' | 'deposit';
  checkAllocations?: CheckAllocationInfo[];
  depositAllocations?: DepositAllocationInfo[];
  loading: boolean;
  error?: string;
};

type ViewFilter =
  | 'all'
  | 'synced'
  | 'ready_to_sync'
  | 'missing_vendor'
  | 'unallocated'
  | 'ignored';

export const QBSyncTab: React.FC<QBSyncTabProps> = ({ clientId }) => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(true);
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const { selectedAccountId, accounts } = useAccountPreference(clientId);
  const { accountMenuItem } = useAccountMenuItem(clientId);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>();
  const [loadingSync, setLoadingSync] = useState<Set<string>>(new Set());
  const [allocationDataMap, setAllocationDataMap] = useState<Map<string, AllocationData>>(new Map());
  const [deletingAllocation, setDeletingAllocation] = useState<string | null>(null);

  // Vendor assignment state
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [selectedCheckAllocation, setSelectedCheckAllocation] = useState<CheckAllocationInfo | null>(null);
  const [selectedLedgerEntryId, setSelectedLedgerEntryId] = useState<string | null>(null);
  const [vendorInput, setVendorInput] = useState<Vendor | string | null>(null);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [payeeIsCustomer, setPayeeIsCustomer] = useState(false);
  const [isAssigningVendor, setIsAssigningVendor] = useState(false);

  // Trust Account Liabilities state
  const [trustAccountConfigured, setTrustAccountConfigured] = useState<boolean>(false);
  const [showTrustAccountDialog, setShowTrustAccountDialog] = useState(false);
  const [pendingSyncEntryId, setPendingSyncEntryId] = useState<string | null>(null);

  // Summary dialog state
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const selectedAccountName = accounts.find((a) => a.id === selectedAccountId)?.name ?? null;

  // Persistent view filter
  const [viewFilter, setViewFilter] = usePersistentState<ViewFilter>(
    'reconciliation:qbSync:viewFilter',
    'all'
  );

  // Persistent filter and sort state
  const {
    sortField,
    sortOrder,
    handleSort,
    filters,
    handleFilter: onFilterChange,
  } = useDataTableState({
    storageKey: 'reconciliation:qbSync',
    defaultSort: { field: 'entry_date', order: -1 },
    defaultFilters: {
      entry_date: { value: null, matchMode: FilterMatchMode.CONTAINS },
      amount: { value: null, matchMode: 'custom_amount' as any },
      entry_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      payment_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
      check_no: { value: null, matchMode: FilterMatchMode.CONTAINS },
      description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  });

  const viewOptions = [
    { label: 'All Entries', value: 'all' },
    { label: 'Synced', value: 'synced' },
    { label: 'Ready to Sync', value: 'ready_to_sync' },
    { label: 'Missing Payee', value: 'missing_vendor' },
    { label: 'Unallocated', value: 'unallocated' },
    { label: 'Ignored', value: 'ignored' },
  ];

  useEffect(() => {
    loadData();
    loadTrustAccountConfig();
  }, [clientId, selectedAccountId, viewFilter]);

  const loadTrustAccountConfig = async () => {
    try {
      const response = await quickbooksApi.getTrustAccountLiabilities(clientId);
      setTrustAccountConfigured(response.is_configured);
    } catch (error) {
      // Silently fail - user will be prompted when they try to sync
      setTrustAccountConfigured(false);
    }
  };

  const handleTrustAccountSelected = (_accountId: string) => {
    setTrustAccountConfigured(true);
    // If there was a pending sync, execute it now
    if (pendingSyncEntryId) {
      const entryId = pendingSyncEntryId;
      setPendingSyncEntryId(null);
      executeSyncToQuickBooks(entryId);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Pass status filter to backend (null for 'all')
      const statusFilter = viewFilter === 'all' ? null : viewFilter;
      const [entries, allocs, vendorsList] = await Promise.all([
        ledgerApi.listLedgerEntries(clientId, { accountId: selectedAccountId, status: statusFilter }),
        allocationsApi.getClientAllocations(clientId, { accountId: selectedAccountId }).catch(() => []),
        vendorsApi.getVendors(clientId).catch(() => []),
      ]);
      setLedgerEntries(entries);
      setAllocations(allocs as Allocation[]);
      setVendors(vendorsList);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load data',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllocation = async (allocationId: string, ledgerEntryId: string) => {
    setDeletingAllocation(allocationId);
    try {
      await allocationsApi.deleteAllocation(clientId, allocationId);
      // Remove from allocations state
      setAllocations(prev => prev.filter(a => a.id !== allocationId));
      // Clear cached allocation data to force refresh on next expand
      setAllocationDataMap(prev => {
        const next = new Map(prev);
        next.delete(ledgerEntryId);
        return next;
      });
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Allocation deleted successfully',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to delete allocation',
        life: 5000,
      });
    } finally {
      setDeletingAllocation(null);
    }
  };

  const confirmDeleteAllocation = (allocationId: string, ledgerEntryId: string, amount: number, customerName?: string) => {
    const formatCurrencyValue = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    confirmDialog({
      message: `Delete allocation of ${formatCurrencyValue(amount)} to ${customerName || 'unknown customer'}?`,
      header: 'Confirm Delete',
      icon: 'ms-out ms-out-warning',
      acceptClassName: 'p-button-danger',
      accept: () => handleDeleteAllocation(allocationId, ledgerEntryId),
    });
  };

  // Payee assignment handlers
  const handleAssignPayeeClick = (allocation: CheckAllocationInfo, ledgerEntryId: string) => {
    setSelectedCheckAllocation(allocation);
    setSelectedLedgerEntryId(ledgerEntryId);
    setVendorInput(null);
    setPayeeIsCustomer(false);
    setShowVendorDialog(true);
  };

  // Handle assign payee from main ledger row - fetches allocations and opens dialog if single, or expands if multiple
  const handleAssignPayeeFromRow = async (entry: LedgerEntry) => {
    // Fetch allocations if not already loaded
    let allocationData = allocationDataMap.get(entry.id);

    if (!allocationData || allocationData.loading) {
      try {
        const checkAllocations = await ledgerApi.getCheckAllocations(clientId, entry.id);
        allocationData = { type: 'check', checkAllocations, loading: false };
        setAllocationDataMap(prev => {
          const next = new Map(prev);
          next.set(entry.id, allocationData!);
          return next;
        });
      } catch {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load allocation data',
          life: 5000,
        });
        return;
      }
    }

    // Find allocations missing payee
    const missingPayee = allocationData.checkAllocations?.filter(a => !a.vendor_name) || [];

    if (missingPayee.length === 1) {
      // Single allocation - open dialog directly
      handleAssignPayeeClick(missingPayee[0], entry.id);
    } else if (missingPayee.length > 1) {
      // Multiple allocations - expand the row
      setExpandedRows(prev => ({
        ...prev,
        [entry.id]: true,
      }));
      toast.current?.show({
        severity: 'info',
        summary: 'Multiple Allocations',
        detail: 'This entry has multiple allocations needing payee assignment',
        life: 3000,
      });
    }
  };

  const searchVendors = (event: AutoCompleteCompleteEvent) => {
    const query = event.query.toLowerCase();
    const filtered = vendors.filter((v) =>
      v.name.toLowerCase().includes(query)
    );
    setFilteredVendors(filtered);
  };

  const handleConfirmVendorAssign = async () => {
    if (!selectedCheckAllocation || !selectedCheckAllocation.check_id) return;

    // If payee is customer, we don't need vendor input
    // If payee is vendor, we need vendor input
    if (!payeeIsCustomer && !vendorInput) return;

    try {
      setIsAssigningVendor(true);

      if (payeeIsCustomer) {
        // Payee is same as customer - just set the flag
        await checksApi.assignVendorToCheck(
          clientId,
          selectedCheckAllocation.check_id,
          null,
          true,
          selectedCheckAllocation.customer_id
        );
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Payee set to customer',
          life: 3000,
        });
      } else {
        // Assign vendor as payee
        let vendorId: string;

        if (typeof vendorInput === 'string') {
          // Create new vendor
          const newVendor = await vendorsApi.createVendor(clientId, {
            name: vendorInput,
          });
          vendorId = newVendor.id;
          setVendors([...vendors, newVendor]);
        } else {
          // Use existing vendor
          vendorId = (vendorInput as Vendor).id;
        }

        await checksApi.assignVendorToCheck(clientId, selectedCheckAllocation.check_id, vendorId, false, null);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Payee assigned successfully',
          life: 3000,
        });
      }

      // Clear cached allocation data to force refresh
      if (selectedLedgerEntryId) {
        setAllocationDataMap(prev => {
          const next = new Map(prev);
          next.delete(selectedLedgerEntryId);
          return next;
        });
      }

      setShowVendorDialog(false);
      setVendorInput(null);
      setPayeeIsCustomer(false);

      // Reload data to update status counts
      await loadData();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.detail || 'Failed to assign payee',
        life: 5000,
      });
    } finally {
      setIsAssigningVendor(false);
    }
  };

  // Build a map of ledger_entry_id -> allocations
  const allocationsByLedgerEntry = useMemo(() => {
    const map = new Map<string, Allocation[]>();
    allocations.forEach((alloc) => {
      if (!map.has(alloc.ledger_entry_id)) {
        map.set(alloc.ledger_entry_id, []);
      }
      map.get(alloc.ledger_entry_id)!.push(alloc);
    });
    return map;
  }, [allocations]);

  // Filtering is now done server-side, so we use ledgerEntries directly
  // This alias is kept for consistency with the rest of the component
  const filteredLedgerEntries = ledgerEntries;

  // Handler for summary dialog filter selection
  const handleSummaryFilterSelect = (status: string) => {
    setViewFilter(status as ViewFilter);
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const hasUnsyncedAllocations = (ledgerEntryId: string): boolean => {
    const entryAllocations = allocationsByLedgerEntry.get(ledgerEntryId);
    return !!entryAllocations && entryAllocations.some((a) => !a.qb_updated_at);
  };

  // Check if this is a partial reconciliation (allocation total < ledger entry amount)
  const isPartialReconciliation = (ledgerEntryId: string): { isPartial: boolean; entryAmount: number; allocationTotal: number } => {
    const entry = ledgerEntries.find(e => e.id === ledgerEntryId);
    const entryAllocations = allocationsByLedgerEntry.get(ledgerEntryId) || [];

    if (!entry) {
      return { isPartial: false, entryAmount: 0, allocationTotal: 0 };
    }

    const entryAmount = Math.abs(Number(entry.amount) || 0);
    const allocationTotal = entryAllocations.reduce((sum, a) => sum + Math.abs(Number(a.amount) || 0), 0);

    // Use a small epsilon for floating point comparison
    const isPartial = allocationTotal < entryAmount - 0.01;

    return { isPartial, entryAmount, allocationTotal };
  };

  // Check if a purchase entry has missing vendor assignments
  const checkMissingVendor = async (entry: LedgerEntry): Promise<boolean> => {
    // Only check for non-deposit entries (purchases/checks)
    if (entry.entry_type === 'deposit') {
      return false;
    }

    // Check if we already have allocation data loaded
    let allocationData = allocationDataMap.get(entry.id);

    // If not loaded, fetch it
    if (!allocationData) {
      try {
        const checkAllocations = await ledgerApi.getCheckAllocations(clientId, entry.id);
        allocationData = { type: 'check', checkAllocations, loading: false };
        setAllocationDataMap(prev => {
          const next = new Map(prev);
          next.set(entry.id, allocationData!);
          return next;
        });
      } catch {
        return false; // Can't determine, allow sync
      }
    }

    // Check if any allocation is missing vendor
    if (allocationData.type === 'check' && allocationData.checkAllocations) {
      return allocationData.checkAllocations.some(alloc => !alloc.vendor_name);
    }

    return false;
  };

  // Check if trust account is configured before syncing
  const handleSync = async (ledgerEntryId: string) => {
    if (!trustAccountConfigured) {
      // Store the pending sync and show dialog
      setPendingSyncEntryId(ledgerEntryId);
      setShowTrustAccountDialog(true);
      return;
    }

    const entry = ledgerEntries.find(e => e.id === ledgerEntryId);
    if (!entry) return;

    // Check for missing vendor on purchase entries
    const hasMissingVendor = await checkMissingVendor(entry);

    if (hasMissingVendor) {
      confirmDialog({
        message: 'This purchase has allocations without a payee assigned. Do you want to proceed anyway?',
        header: 'Missing Payee Warning',
        icon: 'ms-out ms-out-warning icon-warning',
        acceptClassName: 'p-button-warning',
        acceptLabel: 'Proceed',
        rejectLabel: 'Cancel',
        accept: () => proceedWithSync(ledgerEntryId),
      });
      return;
    }

    proceedWithSync(ledgerEntryId);
  };

  // Continue with sync after vendor check
  const proceedWithSync = (ledgerEntryId: string) => {
    // Check for partial reconciliation
    const { isPartial, entryAmount, allocationTotal } = isPartialReconciliation(ledgerEntryId);

    if (isPartial) {
      confirmDialog({
        message: `This is a partial reconciliation. Allocation total (${formatCurrency(allocationTotal)}) is less than the ledger entry amount (${formatCurrency(entryAmount)}). Do you want to proceed?`,
        header: 'Partial Reconciliation Warning',
        icon: 'ms-out ms-out-warning icon-warning',
        acceptClassName: 'p-button-warning',
        acceptLabel: 'Proceed',
        rejectLabel: 'Cancel',
        accept: () => executeSyncToQuickBooks(ledgerEntryId),
      });
      return;
    }

    executeSyncToQuickBooks(ledgerEntryId);
  };

  // Execute the actual sync to QuickBooks
  const executeSyncToQuickBooks = async (ledgerEntryId: string) => {
    setLoadingSync((prev) => new Set(prev).add(ledgerEntryId));
    try {
      await quickbooksApi.syncLedgerEntry(clientId, ledgerEntryId);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Successfully synced to QuickBooks',
        life: 3000,
      });

      // Reload data to update sync status
      await loadData();

      // Clear allocation data cache for this entry to force refresh
      setAllocationDataMap((prev) => {
        const next = new Map(prev);
        next.delete(ledgerEntryId);
        return next;
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to sync to QuickBooks',
        life: 5000,
      });
    } finally {
      setLoadingSync((prev) => {
        const next = new Set(prev);
        next.delete(ledgerEntryId);
        return next;
      });
    }
  };

  const handleFilter = (event: DataTableFilterEvent) => {
    onFilterChange(event);
  };

  const fetchAllocationsForEntry = useCallback(async (entry: LedgerEntry) => {
    const entryId = entry.id;

    // Skip if already loaded or loading
    if (allocationDataMap.has(entryId)) {
      return;
    }

    // Determine type based on entry_type
    const isDeposit = entry.entry_type === 'deposit';
    const type = isDeposit ? 'deposit' : 'check';

    // Set loading state
    setAllocationDataMap(prev => {
      const next = new Map(prev);
      next.set(entryId, { type, loading: true });
      return next;
    });

    try {
      if (isDeposit) {
        const depositAllocations = await ledgerApi.getDepositAllocations(clientId, entryId);
        setAllocationDataMap(prev => {
          const next = new Map(prev);
          next.set(entryId, { type: 'deposit', depositAllocations, loading: false });
          return next;
        });
      } else {
        const checkAllocations = await ledgerApi.getCheckAllocations(clientId, entryId);
        setAllocationDataMap(prev => {
          const next = new Map(prev);
          next.set(entryId, { type: 'check', checkAllocations, loading: false });
          return next;
        });
      }
    } catch (error: any) {
      setAllocationDataMap(prev => {
        const next = new Map(prev);
        next.set(entryId, { type, loading: false, error: error.message || 'Failed to load allocations' });
        return next;
      });
    }
  }, [clientId, allocationDataMap]);

  const handleRowToggle = (e: DataTableRowToggleEvent) => {
    const newExpandedRows = e.data as DataTableExpandedRows;
    setExpandedRows(newExpandedRows);

    // Fetch allocations for newly expanded rows
    if (newExpandedRows) {
      const expandedIds = Object.keys(newExpandedRows);
      expandedIds.forEach(id => {
        const entry = ledgerEntries.find(e => e.id === id);
        if (entry) {
          fetchAllocationsForEntry(entry);
        }
      });
    }
  };

  const rowExpansionTemplate = (entry: LedgerEntry) => {
    const allocationData = allocationDataMap.get(entry.id);

    if (!allocationData || allocationData.loading) {
      return (
        <div className="row-expansion-container">
          <Panel header="Customer Allocations">
            <div className="panel-loading">
              <i className="pi pi-spin pi-spinner" />
            </div>
          </Panel>
        </div>
      );
    }

    if (allocationData.error) {
      return (
        <div className="row-expansion-container">
          <Panel header="Customer Allocations">
            <Message severity="error" text={allocationData.error} />
          </Panel>
        </div>
      );
    }

    // Render deposit allocations
    if (allocationData.type === 'deposit') {
      const deposits = allocationData.depositAllocations || [];
      if (deposits.length === 0) {
        return (
          <div className="row-expansion-container">
            <Panel header="Linked Deposit(s)">
              <Message severity="info" text="No deposit allocations for this entry" />
            </Panel>
          </div>
        );
      }

      return (
        <div className="row-expansion-container">
          <Panel header="Linked Deposit(s)">
            <DataTable value={deposits} size="small">
              <Column
                field="amount"
                header="Allocation $"
                style={{ width: '12%' }}
                body={(rowData: DepositAllocationInfo) => formatCurrency(rowData.amount)}
              />
              <Column field="customer_name" header="Customer" style={{ width: '18%' }} />
              <Column
                field="deposit_date"
                header="Deposit Date"
                style={{ width: '12%' }}
                body={(rowData: DepositAllocationInfo) => formatDate(rowData.deposit_date)}
              />
              <Column
                field="deposit_amount"
                header="Deposit $"
                style={{ width: '12%' }}
                body={(rowData: DepositAllocationInfo) => formatCurrency(rowData.deposit_amount)}
              />
              <Column field="deposit_customer_name" header="Deposit Customer" style={{ width: '15%' }} />
              <Column field="allocation_source" header="Source" style={{ width: '8%' }} />
              <Column
                header="QB Synced"
                style={{ width: '8%' }}
                body={(rowData: DepositAllocationInfo) => {
                  if (rowData.qb_updated_at) {
                    return `Yes`;
                  }
                  return 'No';
                }}
              />
              <Column
                style={{ width: '5%' }}
                body={(rowData: DepositAllocationInfo) => {
                  if (rowData.allocation_source !== 'manual') return null;
                  const isDeleting = deletingAllocation === rowData.id;
                  return (
                    <Button
                      icon={isDeleting ? 'pi pi-spin pi-spinner' : 'ms-out ms-out-delete'}
                      severity="danger"
                      size="small"
                      link
                      disabled={isDeleting}
                      onClick={() => confirmDeleteAllocation(rowData.id, entry.id, rowData.amount, rowData.customer_name ?? undefined)}
                      tooltip="Delete allocation"
                    />
                  );
                }}
              />
            </DataTable>
          </Panel>
        </div>
      );
    }

    // Render check allocations
    const checks = allocationData.checkAllocations || [];
    if (checks.length === 0) {
      return (
        <div className="row-expansion-container">
          <Panel header="Linked Check(s)">
            <Message severity="info" text="No check allocations for this entry" />
          </Panel>
        </div>
      );
    }

    return (
      <div className="row-expansion-container">
        <Panel header="Linked Check(s)">
          <DataTable value={checks} size="small">
            <Column field="check_number" header="Check #" style={{ width: '8%' }} />
            <Column
              field="amount"
              header="Amount"
              style={{ width: '12%' }}
              body={(rowData: CheckAllocationInfo) => formatCurrency(rowData.amount)}
            />
            <Column
              field="check_date"
              header="Date"
              style={{ width: '12%' }}
              body={(rowData: CheckAllocationInfo) => formatDate(rowData.check_date)}
            />
            <Column field="customer_name" header="Customer" style={{ width: '20%' }} />
            <Column
              field="vendor_name"
              header="Payee"
              style={{ width: '20%' }}
              body={(rowData: CheckAllocationInfo) => {
                if (rowData.vendor_name) {
                  return rowData.vendor_name;
                }
                return (
                  <Button
                    label="Assign Payee"
                    icon="ms-out ms-out-storefront"
                    size="small"
                    link
                    onClick={() => handleAssignPayeeClick(rowData, entry.id)}
                    disabled={isAssigningVendor}
                  />
                );
              }}
            />
            <Column
              header="QB Synced"
              style={{ width: '8%' }}
              body={(rowData: CheckAllocationInfo) => {
                if (rowData.qb_updated_at) {
                  return `Yes`;
                }
                return 'No';
              }}
            />
            <Column
              style={{ width: '5%' }}
              body={(rowData: CheckAllocationInfo) => {
                if (rowData.allocation_source !== 'manual') return null;
                const isDeleting = deletingAllocation === rowData.id;
                return (
                  <Button
                    icon={isDeleting ? 'pi pi-spin pi-spinner' : 'ms-out ms-out-delete'}
                    severity="danger"
                    size="small"
                    link
                    disabled={isDeleting}
                    onClick={() => confirmDeleteAllocation(rowData.id, entry.id, rowData.amount, rowData.customer_name ?? undefined)}
                    tooltip="Delete allocation"
                  />
                );
              }}
            />
          </DataTable>
        </Panel>
      </div>
    );
  };

  const dateBodyTemplate = (rowData: LedgerEntry) => formatDate(rowData.entry_date);

  const amountBodyTemplate = (rowData: LedgerEntry) => {
    return formatCurrency(rowData.amount);
  };

  const hasSyncedAllocations = (ledgerEntryId: string): boolean => {
    const entryAllocations = allocationsByLedgerEntry.get(ledgerEntryId);
    return !!entryAllocations && entryAllocations.length > 0 && entryAllocations.every((a) => a.qb_updated_at);
  };

  const actionsBodyTemplate = (rowData: LedgerEntry) => {
    const isLoading = loadingSync.has(rowData.id);
    const hasUnsynced = hasUnsyncedAllocations(rowData.id);
    const hasSynced = hasSyncedAllocations(rowData.id);
    const isCheckEntry = rowData.entry_type !== 'deposit';

    // Check if this entry has missing payee (only for check entries when viewing missing_vendor filter)
    const showAssignPayee = isCheckEntry && viewFilter === 'missing_vendor';

    if (isLoading) {
      return (
        <div className="row-syncing">
          <i className="pi pi-spin pi-spinner" />
          <span className="syncing-text">Syncing...</span>
        </div>
      );
    }

    return (
      <div className="row-actions">
        {showAssignPayee && (
          <Button
            label="Assign Payee"
            icon="ms-out ms-out-storefront"
            size="small"
            link
            onClick={() => handleAssignPayeeFromRow(rowData)}
            disabled={isAssigningVendor}
          />
        )}
        {(hasUnsynced || hasSynced) && (
          <Button
            label={hasUnsynced ? 'Sync' : 'Resync'}
            icon="ms-out ms-out-sync"
            size="small"
            link
            onClick={() => handleSync(rowData.id)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <TrustAccountSelectionDialog
        clientId={clientId}
        visible={showTrustAccountDialog}
        onHide={() => {
          setShowTrustAccountDialog(false);
          setPendingSyncEntryId(null);
        }}
        onAccountSelected={handleTrustAccountSelected}
      />

      <LedgerEntrySummaryDialog
        clientId={clientId}
        accountId={selectedAccountId}
        accountName={selectedAccountName}
        visible={showSummaryDialog}
        onHide={() => setShowSummaryDialog(false)}
        onFilterSelect={handleSummaryFilterSelect}
      />

      {/* Assign Vendor Dialog */}
      <Dialog
        header="Assign Check Payee"
        visible={showVendorDialog}
        className="customer-assignment-dialog"
        onHide={() => setShowVendorDialog(false)}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              onClick={() => setShowVendorDialog(false)}
              severity="secondary"
            />
            <Button
              label="Assign"
              severity="success"
              onClick={handleConfirmVendorAssign}
              loading={isAssigningVendor}
              disabled={!payeeIsCustomer && !vendorInput}
            />
          </div>
        }
      >
        <div>
          <div className="dialog-context-info">
            <div className="context-row">
              <span className="context-label">Payee (from Check):</span> {selectedCheckAllocation?.payee_name || '—'}
            </div>
            {selectedCheckAllocation?.memo && (
              <div className="context-row">
                <span className="context-label">Memo:</span> {selectedCheckAllocation.memo}
              </div>
            )}
          </div>

          <div className="dialog-checkbox-row">
            <Checkbox
              inputId="payeeIsCustomerQbSync"
              checked={payeeIsCustomer}
              onChange={(e: CheckboxChangeEvent) => setPayeeIsCustomer(e.checked ?? false)}
              disabled={!selectedCheckAllocation?.customer_id}
            />
            <label htmlFor="payeeIsCustomerQbSync">
              Payee is same as customer
              {selectedCheckAllocation?.customer_name && ` (${selectedCheckAllocation.customer_name})`}
            </label>
          </div>

          {!payeeIsCustomer && (
            <>
              <div className="customer-assignment-label-row">
                <label className="customer-assignment-label">Payee</label>
                <Button
                  label="+ New"
                  link
                  size="small"
                  onClick={() => {
                    if (selectedCheckAllocation?.payee_name) {
                      setVendorInput(selectedCheckAllocation.payee_name);
                    }
                  }}
                />
              </div>
              <AutoComplete
                value={vendorInput}
                suggestions={filteredVendors}
                completeMethod={searchVendors}
                field="name"
                onChange={(e) => setVendorInput(e.value)}
                placeholder="Select or type new payee name"
                className="customer-assignment-input"
                dropdown
              />
            </>
          )}
        </div>
      </Dialog>

      <Menubar
        className="actions-menubar"
        model={[
          accountMenuItem,
          {
            label: `View: ${viewOptions.find(o => o.value === viewFilter)?.label || 'All'}`,
            icon: 'ms-out ms-out-filter-list',
            items: viewOptions.map(option => ({
              label: option.label,
              icon: viewFilter === option.value ? 'ms-out ms-out-check' : undefined,
              command: () => setViewFilter(option.value as ViewFilter),
            })),
          },
          {
            label: trustAccountConfigured ? 'Trust Account' : 'Configure Trust Account',
            icon: trustAccountConfigured ? 'ms-out ms-out-check' : 'ms-out ms-out-settings',
            command: () => setShowTrustAccountDialog(true),
          },
        ]}
        end={
          <Button
            label="Summary"
            icon="ms-out ms-out-bar-chart"
            size="small"
            outlined
            onClick={() => setShowSummaryDialog(true)}
          />
        }
      />

      {filteredLedgerEntries.length === 0 && !loading ? (
        <Message
          severity="info"
          text={
            viewFilter === 'all'
              ? 'No ledger entries found.'
              : `No ledger entries with status "${viewOptions.find(o => o.value === viewFilter)?.label}" found.`
          }
        />
      ) : (
        <DataTable
          value={filteredLedgerEntries}
          dataKey="id"
          size="small"
          scrollable
          scrollHeight="flex"
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          loading={loading}
          expandedRows={expandedRows}
          onRowToggle={handleRowToggle}
          stripedRows
          rowExpansionTemplate={rowExpansionTemplate}
          filterDisplay="menu"
          filters={filters}
          onFilter={handleFilter}
          sortField={sortField ?? undefined}
          sortOrder={sortOrder ?? undefined}
          onSort={handleSort}
          paginatorTemplate={{
            layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
            CurrentPageReport: (options) => (
              <span className="paginator-current">
                Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> entries
              </span>
            ),
          }}
        >
          <Column expander style={{ width: '3%' }} />
          <Column
            field="entry_date"
            header="Date"
            style={{ width: '12%' }}
            sortable
            filter
            filterPlaceholder="Search by date"
            showFilterMatchModes={false}
            body={dateBodyTemplate}
          />
          <Column
            field="amount"
            header="Amount"
            style={{ width: '12%' }}
            sortable
            filter
            filterPlaceholder="Search by amount"
            showFilterMatchModes={false}
            filterFunction={amountFilterFunction}
            body={amountBodyTemplate}
          />
          <Column
            field="entry_type"
            header="Type"
            style={{ width: '10%' }}
            sortable
            filter
            filterPlaceholder="Search by type"
            showFilterMatchModes={false}
          />
          <Column
            field="payment_type"
            header="Payment Type"
            style={{ width: '20%' }}
            sortable
            filter
            filterPlaceholder="Search by payment type"
            showFilterMatchModes={false}
          />
          <Column
            field="check_no"
            header="Check #"
            style={{ width: '15%' }}
            sortable
            filter
            filterPlaceholder="Search by check no"
            showFilterMatchModes={false}
          />
          <Column
            field="description"
            header="Description"
            sortable
            filter
            filterPlaceholder="Search by description"
            showFilterMatchModes={false}
          />
          <Column style={{ width: '10%' }} body={actionsBodyTemplate} />
        </DataTable>
      )}
    </>
  );
};
